import React from 'react';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import AppCheckbox from './AppCheckbox';

export interface AppCheckboxFormLabelProps {
  /** Explicit id for the checkbox input; auto-generated if omitted. */
  id?: string;
  label: string;
  required?: boolean;
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (e: CheckboxChangeEvent) => void;
  disabled?: boolean;
  /** Applied to the outer wrapper div. */
  className?: string;
  tabIndex?: number;
}

/**
 * AppCheckboxFormLabel
 *
 * Checkbox + label text + optional 必須 badge, placed OUTSIDE AntD's <label>
 * wrapper so the wrapper does not stretch over the badge area.
 *
 * Layout: [checkbox] [label text] [........] [必須]
 *
 * - The sibling <label htmlFor> wraps only the text → compact click area, clicking toggles checkbox.
 * - The 必須 badge is a sibling outside <label> → clicking badge does NOT toggle checkbox.
 * - ml-auto on badge pushes it to the end of the flex wrapper.
 * - flex-1 on the wrapper (via className prop) makes ml-auto work.
 *
 * No nested <label> elements.
 */
const AppCheckboxFormLabel: React.FC<AppCheckboxFormLabelProps> = ({
  id: idProp,
  label,
  required = false,
  className,
  tabIndex = -1,
  ...checkboxProps
}) => {
  const autoId = React.useId();
  const id = idProp ?? autoId;

  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`} data-skip-enter-nav>
      <AppCheckbox
        id={id}
        tabIndex={tabIndex}
        className="app-checkbox-checked-primary-outline"
        {...checkboxProps}
      />
      <label
        htmlFor={id}
        className="font-medium text-gray-700 cursor-pointer select-none whitespace-nowrap"
      >
        {label}
      </label>
      {required && (
        <span className="ml-auto px-1.5 py-0.5 text-xs bg-pink-500 text-white rounded whitespace-nowrap">
          必須
        </span>
      )}
    </div>
  );
};

AppCheckboxFormLabel.displayName = 'AppCheckboxFormLabel';

export default AppCheckboxFormLabel;
